import { forwardRef, SyntheticEvent, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocalStorage } from 'react-use';

import ArrowDropDownCircleOutlined from '@mui/icons-material/ArrowDropDownCircleOutlined';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  AccordionProps,
  AccordionSummaryProps,
} from '@mui/material';

import { Issuer } from '@arrived/models';

import { ReservationDisclaimer } from './ReservationDisclaimer';
import { StandardDisclaimer } from './StandardDisclaimer';

interface DisclaimerAccordionProps extends Omit<AccordionProps, 'children'> {
  AccordionSummaryProps?: AccordionSummaryProps;
  isReserve?: boolean;
  issuerId?: Issuer['id'];
}

export const DisclaimerAccordion = forwardRef<HTMLDivElement, DisclaimerAccordionProps>(
  ({ AccordionSummaryProps, isReserve, issuerId = 1, sx, ...rest }, ref) => {
    const [isExpanded, setIsExpanded] = useLocalStorage('importantInfoAccordion-expanded', true);

    const handleOnChange = useCallback(
      (_: SyntheticEvent, expanded: boolean) => setIsExpanded(expanded),
      [setIsExpanded]
    );

    const { sx: AccordionSummaryPropsSx, ...AccordionSummaryPropsRest } = AccordionSummaryProps || {};

    return (
      <Accordion
        defaultExpanded
        expanded={isExpanded}
        square={true}
        elevation={0}
        onChange={handleOnChange}
        sx={{ backgroundColor: ({ palette }) => palette.lightened.bare, '&::before': { display: 'none' }, ...sx }}
        ref={ref}
        {...rest}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownCircleOutlined />}
          sx={{
            px: { xs: 3, md: 12.5 },
            '& .MuiAccordionSummary-content': { my: 2 },
            ...AccordionSummaryPropsSx,
          }}
          {...AccordionSummaryPropsRest}
        >
          <Typography variant="h6">
            <FormattedMessage id="footer-important-information-title" defaultMessage="Important Information" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 3, md: 12.5 } }}>
          <Typography component="div" variant="body1" gutterBottom>
            {isReserve ? <ReservationDisclaimer /> : <StandardDisclaimer issuerId={issuerId} />}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }
);
